/**
 * useHardwareQrScanner (built on @use-symbology-scanner/react)
 * ---------------------------------------------------------------------------
 * A non-blocking hook for physical/USB/Bluetooth QR & barcode scanners.
 * These devices act as "keyboard wedges": they type the decoded value very
 * fast, then send Enter/Tab. This hook uses a purpose-built, actively
 * maintained library that recognizes real barcode symbologies (rather than
 * a hand-rolled "fast keystrokes = scan" heuristic), and listens passively
 * on the whole document — so it NEVER blocks clicks, typing, or any other
 * interaction elsewhere on the page. The user can keep using the rest of
 * your UI while a scan happens in the background.
 *
 * Install dependencies:
 *   npm install @use-symbology-scanner/react @use-symbology-scanner/core
 *   # Only needed if you also want the ready-made toast feedback below:
 *   npm install @mui/material @mui/icons-material @emotion/react @emotion/styled
 *
 * Basic usage:
 * ------------
 * import { useHardwareQrScanner } from './useHardwareQrScanner';
 *
 * function MyPage() {
 *   const { lastScan } = useHardwareQrScanner({
 *     onScan: (code) => console.log('Scanned:', code),
 *   });
 *
 *   return (
 *     <div>
 *       // The rest of your page — forms, buttons, inputs — all work
 *       // completely normally while scanning is active in the background.
 *       <MyForm />
 *       <MyOtherWidgets />
 *     </div>
 *   );
 * }
 *
 * With the built-in non-blocking toast feedback:
 * -----------------------------------------------
 * import { useHardwareQrScanner, ScanFeedbackToast } from './useHardwareQrScanner';
 *
 * function MyPage() {
 *   const scanner = useHardwareQrScanner({
 *     onScan: (code) => addItemToCart(code),
 *   });
 *
 *   return (
 *     <div>
 *       <MyForm />
 *       <ScanFeedbackToast scanner={scanner} />
 *     </div>
 *   );
 * }
 * ---------------------------------------------------------------------------
 */

import React, { useCallback, useRef, useState } from 'react';
import { useSymbologyScanner } from '@use-symbology-scanner/react';
import { Snackbar, Alert } from '@mui/material';

export function useHardwareQrScanner(options) {
  const {
    onScan,
    enabled = true,
    symbologies = [{ allowedCharacters: /[\x00-\x7F]/ }],
    preventDuplicateMs = 1000,
    target,
  } = options;

  const [lastScan, setLastScan] = useState(null);
  const [scanCount, setScanCount] = useState(0);

  const lastScanRef = useRef(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan; // always call the latest callback

  const reset = useCallback(() => setLastScan(null), []);

  const handleSymbol = useCallback(
    (symbol) => {
      const now = Date.now();
      const previous = lastScanRef.current;

      if (previous && previous.value === symbol && now - previous.time < preventDuplicateMs) {
        return; // duplicate scan within the debounce window — ignore
      }

      lastScanRef.current = { value: symbol, time: now };
      const value = Number(String(symbol).replace(`"type":"bon§commande","id":`,'').replace("—",""));
      setLastScan(value);
      setScanCount((c) => c + 1);
      console.log("symbol:",symbol)
      console.log("value",value)
      onScanRef.current(value);
    },
    [preventDuplicateMs],
  );

  useSymbologyScanner(handleSymbol, {
    // Falls back to `window.document` internally when no target is given —
    // this is what lets scanning work anywhere on the page without
    // requiring focus, while every other element stays fully interactive.
    target,
    symbologies,
    enabled,
    // Never intercept the event before other listeners get it, and never
    // block default browser behavior (typing, clicking, tabbing, etc.).
    preventDefault: false,
    eventOptions: { capture: false, passive: true },
  });

  return { lastScan, scanCount, isEnabled: enabled, reset };
}

/**
 * ScanFeedbackToast
 * ---------------------------------------------------------------------------
 * A drop-in, non-blocking visual confirmation for scans. It's a plain MUI
 * Snackbar/Alert — auto-dismisses, doesn't trap focus, and never sits on
 * top of interactive content in a way that would block the user from
 * clicking through it.
 * ---------------------------------------------------------------------------
 */

export const ScanFeedbackToast = ({ scanner, autoHideDuration = 2000 }) => {
  const { lastScan, scanCount, reset } = scanner;

  return (
    <Snackbar
      key={scanCount} // re-triggers the toast on every new scan, even repeats after the window
      open={Boolean(lastScan)}
      autoHideDuration={autoHideDuration}
      onClose={reset}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert onClose={reset} severity="success" variant="filled">
        Scanned: {lastScan}
      </Alert>
    </Snackbar>
  );
};
